import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Smartphone, User, Loader2, CreditCard } from "lucide-react";

interface MobileDevice {
  id: string;
  phone_type: string;
  serial_number: string | null;
  imei: string | null;
  model: string | null;
  manufacturer: string | null;
  storage_capacity: string | null;
  color: string | null;
  warranty_end_date: string | null;
  condition: string | null;
  email: string | null;
  purchase_cost: number;
  assigned_employee_id: string | null;
  assigned_at: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
}

interface Employee {
  id: string;
  name: string;
  position: string;
}

interface SimCard {
  id: string;
  sim_number: string;
  phone_number: string | null;
  provider: string;
  assigned_device_id: string | null;
}

const MANUFACTURERS = [
  "Apple",
  "Samsung",
  "Google",
  "Huawei",
  "Xiaomi",
  "OnePlus",
  "Sony",
  "Nokia",
  "Motorola",
  "Sonstiger",
];

const CONDITIONS = [
  { value: "neu", label: "Neu" },
  { value: "sehr_gut", label: "Sehr gut" },
  { value: "gut", label: "Gut" },
  { value: "akzeptabel", label: "Akzeptabel" },
  { value: "defekt", label: "Defekt" },
];

export default function MobileDeviceSettings() {
  const { user } = useAuth();
  const [devices, setDevices] = useState<MobileDevice[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [simCards, setSimCards] = useState<SimCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<MobileDevice | null>(null);
  const [deviceToDelete, setDeviceToDelete] = useState<MobileDevice | null>(null);
  const [deviceToAssign, setDeviceToAssign] = useState<MobileDevice | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");

  const [formData, setFormData] = useState({
    phone_type: "",
    serial_number: "",
    imei: "",
    model: "",
    manufacturer: "",
    storage_capacity: "",
    color: "",
    warranty_end_date: "",
    condition: "neu",
    email: "",
    purchase_cost: "",
    notes: "",
    assigned_employee_id: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [devicesRes, employeesRes, simCardsRes] = await Promise.all([
        supabase
          .from("mobile_phones")
          .select("id, phone_type, serial_number, imei, model, manufacturer, storage_capacity, color, warranty_end_date, condition, email, purchase_cost, assigned_employee_id, assigned_at, notes, is_active, created_at")
          .order("created_at", { ascending: false }),
        supabase
          .from("employees")
          .select("id, name, position")
          .is("archived_at", null)
          .order("name"),
        supabase
          .from("sim_cards")
          .select("id, sim_number, phone_number, provider, assigned_device_id")
          .eq("is_active", true),
      ]);

      if (devicesRes.error) throw devicesRes.error;
      if (employeesRes.error) throw employeesRes.error;
      if (simCardsRes.error) throw simCardsRes.error;

      setDevices(devicesRes.data || []);
      setEmployees(employeesRes.data || []);
      setSimCards(simCardsRes.data || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      phone_type: "",
      serial_number: "",
      imei: "",
      model: "",
      manufacturer: "",
      storage_capacity: "",
      color: "",
      warranty_end_date: "",
      condition: "neu",
      email: "",
      purchase_cost: "",
      notes: "",
      assigned_employee_id: "",
    });
    setEditingDevice(null);
  };

  const handleOpenDialog = (device?: MobileDevice) => {
    if (device) {
      setEditingDevice(device);
      setFormData({
        phone_type: device.phone_type,
        serial_number: device.serial_number || "",
        imei: device.imei || "",
        model: device.model || "",
        manufacturer: device.manufacturer || "",
        storage_capacity: device.storage_capacity || "",
        color: device.color || "",
        warranty_end_date: device.warranty_end_date || "",
        condition: device.condition || "neu",
        email: device.email || "",
        purchase_cost: device.purchase_cost ? String(device.purchase_cost) : "",
        notes: device.notes || "",
        assigned_employee_id: device.assigned_employee_id || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.phone_type.trim()) {
      toast.error("Bitte geben Sie den Gerätetyp ein");
      return;
    }

    setSaving(true);
    try {
      const purchaseCost = formData.purchase_cost ? parseFloat(formData.purchase_cost) : 0;
      const assignedEmployeeId = formData.assigned_employee_id || null;

      if (editingDevice) {
        const { error } = await supabase
          .from("mobile_phones")
          .update({
            phone_type: formData.phone_type,
            serial_number: formData.serial_number || null,
            imei: formData.imei || null,
            model: formData.model || null,
            manufacturer: formData.manufacturer || null,
            storage_capacity: formData.storage_capacity || null,
            color: formData.color || null,
            warranty_end_date: formData.warranty_end_date || null,
            condition: formData.condition || null,
            email: formData.email || null,
            purchase_cost: purchaseCost,
            notes: formData.notes || null,
            assigned_employee_id: assignedEmployeeId,
            assigned_at: assignedEmployeeId !== editingDevice.assigned_employee_id 
              ? (assignedEmployeeId ? new Date().toISOString() : null) 
              : editingDevice.assigned_at,
          })
          .eq("id", editingDevice.id);

        if (error) throw error;
        toast.success("Gerät wurde aktualisiert");
      } else {
        const { error } = await supabase.from("mobile_phones").insert({
          phone_type: formData.phone_type,
          serial_number: formData.serial_number || null,
          imei: formData.imei || null,
          model: formData.model || null,
          manufacturer: formData.manufacturer || null,
          storage_capacity: formData.storage_capacity || null,
          color: formData.color || null,
          warranty_end_date: formData.warranty_end_date || null,
          condition: formData.condition || null,
          email: formData.email || null,
          purchase_cost: purchaseCost,
          notes: formData.notes || null,
          assigned_employee_id: assignedEmployeeId,
          assigned_at: assignedEmployeeId ? new Date().toISOString() : null,
          created_by: user?.id,
          monthly_cost: 0, // Devices don't have monthly cost, SIM cards do
        });

        if (error) throw error;
        toast.success("Gerät wurde hinzugefügt");
      }

      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error saving device:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deviceToDelete) return;

    try {
      const { error } = await supabase
        .from("mobile_phones")
        .delete()
        .eq("id", deviceToDelete.id);

      if (error) throw error;
      toast.success("Gerät wurde gelöscht");
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
      loadData();
    } catch (error) {
      console.error("Error deleting device:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleAssign = async () => {
    if (!deviceToAssign) return;

    try {
      const { error } = await supabase
        .from("mobile_phones")
        .update({
          assigned_employee_id: selectedEmployeeId || null,
          assigned_at: selectedEmployeeId ? new Date().toISOString() : null,
        })
        .eq("id", deviceToAssign.id);

      if (error) throw error;
      toast.success(
        selectedEmployeeId
          ? "Gerät wurde zugewiesen"
          : "Zuweisung wurde entfernt"
      );
      setAssignDialogOpen(false);
      setDeviceToAssign(null);
      setSelectedEmployeeId("");
      loadData();
    } catch (error) {
      console.error("Error assigning device:", error);
      toast.error("Fehler bei der Zuweisung");
    }
  };

  const openAssignDialog = (device: MobileDevice) => {
    setDeviceToAssign(device);
    setSelectedEmployeeId(device.assigned_employee_id || "");
    setAssignDialogOpen(true);
  };

  const getEmployeeName = (employeeId: string | null) => {
    if (!employeeId) return null;
    const employee = employees.find((e) => e.id === employeeId);
    return employee?.name || "Unbekannt";
  };

  const getAssignedSimCard = (deviceId: string) => {
    return simCards.find(s => s.assigned_device_id === deviceId);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getConditionLabel = (condition: string | null) => {
    if (!condition) return "-";
    const found = CONDITIONS.find(c => c.value === condition);
    return found?.label || condition;
  };

  // Calculate totals
  const totalPurchase = devices.reduce((sum, d) => sum + (d.purchase_cost || 0), 0);
  const assignedCount = devices.filter((d) => d.assigned_employee_id).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Gesamt Geräte
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{devices.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Zugewiesen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{assignedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Anschaffungskosten
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalPurchase)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Action Button */}
      <div className="flex justify-end">
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="mr-2 h-4 w-4" />
          Gerät hinzufügen
        </Button>
      </div>

      {/* Device List */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Gerät</TableHead>
                <TableHead>Hersteller</TableHead>
                <TableHead>IMEI / Seriennr.</TableHead>
                <TableHead>Zustand</TableHead>
                <TableHead>SIM-Karte</TableHead>
                <TableHead className="text-right">Kaufpreis</TableHead>
                <TableHead>Zugewiesen an</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    Keine Geräte vorhanden
                  </TableCell>
                </TableRow>
              ) : (
                devices.map((device) => {
                  const assignedSim = getAssignedSimCard(device.id);
                  return (
                    <TableRow key={device.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Smartphone className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div>{device.phone_type}</div>
                            {device.model && (
                              <div className="text-xs text-muted-foreground">{device.model}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{device.manufacturer || "-"}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {device.imei && <div>IMEI: {device.imei}</div>}
                          {device.serial_number && <div>SN: {device.serial_number}</div>}
                          {!device.imei && !device.serial_number && "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={device.condition === 'defekt' ? 'destructive' : 'secondary'}>
                          {getConditionLabel(device.condition)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {assignedSim ? (
                          <Badge variant="outline" className="gap-1">
                            <CreditCard className="h-3 w-3" />
                            {assignedSim.provider}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">Keine SIM</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(device.purchase_cost || 0)}
                      </TableCell>
                      <TableCell>
                        {device.assigned_employee_id ? (
                          <Badge variant="secondary" className="gap-1">
                            <User className="h-3 w-3" />
                            {getEmployeeName(device.assigned_employee_id)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">Nicht zugewiesen</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openAssignDialog(device)}
                            title="Zuweisen"
                          >
                            <User className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(device)}
                            title="Bearbeiten"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setDeviceToDelete(device);
                              setDeleteDialogOpen(true);
                            }}
                            title="Löschen"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDevice ? "Gerät bearbeiten" : "Neues Gerät hinzufügen"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone_type">Gerätebezeichnung *</Label>
                <Input
                  id="phone_type"
                  value={formData.phone_type}
                  onChange={(e) => setFormData({ ...formData, phone_type: e.target.value })}
                  placeholder="z.B. iPhone 15 Pro"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="manufacturer">Hersteller</Label>
                <Select
                  value={formData.manufacturer}
                  onValueChange={(value) => setFormData({ ...formData, manufacturer: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Hersteller wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {MANUFACTURERS.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="model">Modell</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="z.B. A2849"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="storage_capacity">Speicherkapazität</Label>
                <Input
                  id="storage_capacity"
                  value={formData.storage_capacity}
                  onChange={(e) => setFormData({ ...formData, storage_capacity: e.target.value })}
                  placeholder="z.B. 256 GB"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="imei">IMEI</Label>
                <Input
                  id="imei"
                  value={formData.imei}
                  onChange={(e) => setFormData({ ...formData, imei: e.target.value })}
                  placeholder="15-stellige IMEI"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="serial_number">Seriennummer</Label>
                <Input
                  id="serial_number"
                  value={formData.serial_number}
                  onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="color">Farbe</Label>
                <Input
                  id="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="z.B. Space Black"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="condition">Zustand</Label>
                <Select
                  value={formData.condition}
                  onValueChange={(value) => setFormData({ ...formData, condition: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Zustand wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="purchase_cost">Kaufpreis (€)</Label>
                <Input
                  id="purchase_cost"
                  type="number"
                  step="0.01"
                  value={formData.purchase_cost}
                  onChange={(e) => setFormData({ ...formData, purchase_cost: e.target.value })}
                  placeholder="0,00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="warranty_end_date">Garantie bis</Label>
                <Input
                  id="warranty_end_date"
                  type="date"
                  value={formData.warranty_end_date}
                  onChange={(e) => setFormData({ ...formData, warranty_end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Verknüpftes E-Mail-Konto</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="z.B. firmenhandy@firma.de"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="assigned_employee_id">Zugewiesen an</Label>
              <Select
                value={formData.assigned_employee_id}
                onValueChange={(value) => setFormData({ ...formData, assigned_employee_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nicht zugewiesen</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} ({emp.position})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notizen</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Zusätzliche Informationen..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingDevice ? "Speichern" : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gerät zuweisen</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              {deviceToAssign?.phone_type} {deviceToAssign?.model && `(${deviceToAssign.model})`}
            </p>
            <Label htmlFor="employee_select">Mitarbeiter</Label>
            <Select
              value={selectedEmployeeId}
              onValueChange={setSelectedEmployeeId}
            >
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Mitarbeiter wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Zuweisung entfernen</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name} ({emp.position})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleAssign}>Zuweisen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gerät löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie das Gerät "{deviceToDelete?.phone_type}" wirklich löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
